import React from "react";
import type {
  DraftPayload,
  PullPlanSectionContext,
  ScopeMember,
} from "../../../../models";
import {
  deleteWorkCode,
  editWorkCode,
  removeFromWorkCode,
  stageTaktifications,
} from "../../draft/draftOps";
import type { ActivityInstance } from "../../draft/types";
import { hashColor, isPlaceholder, isTaskType, type PaletteActivity } from "../helpers";

interface SeedPalette {
  palette: PaletteActivity[];
}

interface Args {
  context: PullPlanSectionContext;
  initial: SeedPalette;
  pendingOps: DraftPayload;
  setPendingOps: React.Dispatch<React.SetStateAction<DraftPayload>>;
  instances: ActivityInstance[];
  setInstances: React.Dispatch<React.SetStateAction<ActivityInstance[]>>;
  protoMembers: ScopeMember[];
  pushUndo: (label?: string) => void;
  dropInstancesFor: (ids: Set<number>) => void;
  // Owned by the host (it rides in the undo snapshot + autosave payload).
  paletteOrder: string[];
  setPaletteOrder: React.Dispatch<React.SetStateAction<string[]>>;
}

export interface WorkCodePalette {
  palette: PaletteActivity[];
  paletteByKey: Map<string, PaletteActivity>;
  visiblePalette: PaletteActivity[];
  codeDisplay: Record<string, string>;
  effectiveCodeColors: Record<string, string>;
  codeEditTarget: string | null;
  setCodeEditTarget: React.Dispatch<React.SetStateAction<string | null>>;
  codeEditActivities: Array<{
    id: number;
    task_code: string;
    task_name: string;
    takt_a: string;
    takt_b: string;
    takt_c: string;
  }>;
  handleAddCodeToPalette: (code: string, name: string, color: string) => void;
  handleEditWorkCode: (
    code: string,
    patch: { label?: string; color?: string; newCode?: string },
  ) => void;
  handleDeleteCode: (code: string) => void;
  handleRemoveFromCode: (activityId: number) => void;
}

export function useWorkCodePalette({
  context,
  initial,
  pendingOps,
  setPendingOps,
  instances,
  setInstances,
  protoMembers,
  pushUndo,
  dropInstancesFor,
  paletteOrder,
  setPaletteOrder,
}: Args): WorkCodePalette {
  const [codeEditTarget, setCodeEditTarget] = React.useState<string | null>(null);

  // Palette (legend chips) DERIVED from committed codes + staged ops, so it's
  // correct whether the draft was just edited or freshly reloaded — a chip per
  // committed code, plus staged work-code adds, plus a fallback for any code a
  // placed bar references that nothing else named. Avoids syncing palette state
  // imperatively (which dropped staged chips on reload). Newly-added codes are
  // pinned to the TOP (newest first) so a just-added code is easy to find; the
  // rest stay alphabetical.
  const palette = React.useMemo<PaletteActivity[]>(() => {
    const m = new Map<string, PaletteActivity>();
    for (const p of initial.palette) m.set(p.activityKey, p);
    const stagedCodes: string[] = [];
    for (const w of pendingOps.pending_work_code_adds) {
      if (!m.has(w.code)) {
        m.set(w.code, {
          activityKey: w.code,
          name: w.prefix || w.label || w.code,
          color: context.takt_work_colors[w.code] ?? w.color ?? hashColor(w.code),
          default_duration: 1,
        });
      }
      stagedCodes.push(w.code);
    }
    for (const inst of instances) {
      if (!m.has(inst.activityKey)) {
        m.set(inst.activityKey, {
          activityKey: inst.activityKey,
          name: inst.activityKey,
          color: context.takt_work_colors[inst.activityKey] ?? hashColor(inst.activityKey),
          default_duration: 1,
        });
      }
    }
    // Section-local rename/recolor/re-code overrides the committed chip's name,
    // color, and displayed code value. The chip stays keyed by the committed
    // code (activityKey) so bars/instances/edit-target keep resolving; only the
    // shown `code` diverges until publish re-codes the carriers.
    for (const e of pendingOps.pending_work_code_edits) {
      const chip = m.get(e.code);
      if (chip) {
        m.set(e.code, {
          ...chip,
          code: e.new_code && e.new_code !== e.code ? e.new_code : chip.code,
          name: e.label != null ? e.label : chip.name,
          color: e.color != null ? e.color : chip.color,
        });
      }
    }
    // A code deleted from this section drops its chip from the legend.
    for (const d of pendingOps.pending_work_code_deletes) m.delete(d.code);
    // Last-added first (the draft-persisted order), the rest alphabetical.
    const orderIdx = new Map(paletteOrder.map((c, i) => [c, i]));
    const listed: PaletteActivity[] = [];
    const rest: PaletteActivity[] = [];
    for (const p of Array.from(m.values())) {
      if (orderIdx.has(p.activityKey)) listed.push(p);
      else rest.push(p);
    }
    listed.sort(
      (a, b) => (orderIdx.get(a.activityKey) ?? 0) - (orderIdx.get(b.activityKey) ?? 0),
    );
    rest.sort((a, b) => a.name.localeCompare(b.name));
    return [...listed, ...rest];
  }, [
    initial.palette,
    pendingOps.pending_work_code_adds,
    pendingOps.pending_work_code_edits,
    pendingOps.pending_work_code_deletes,
    instances,
    context.takt_work_colors,
    paletteOrder,
  ]);

  const paletteByKey = React.useMemo(() => {
    const m = new Map<string, PaletteActivity>();
    for (const p of palette) m.set(p.activityKey, p);
    return m;
  }, [palette]);

  // "+ Add work code": put a picked / freshly-created code's chip on the
  // palette (staged; publish mints the work_codes row). Dragging the chip onto
  // a level is what creates activities.
  const handleAddCodeToPalette = React.useCallback(
    (code: string, name: string, color: string) => {
      if (paletteByKey.has(code)) return;
      pushUndo();
      setPaletteOrder((prev) => [code, ...prev.filter((c) => c !== code)]);
      setPendingOps((prev) =>
        stageTaktifications(prev, [], [
          { code, prefix: name, label: name, color, default_duration_days: 3 },
        ]),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pushUndo],
  );

  // The activities carrying the legend code currently being edited, shaped for
  // CodeEditModal. Merges committed (a.takt_work === code) with draft-staged
  // (a pending taktification assigned this code) — so a freshly auto-takted
  // code still lists its activities before publish.
  const codeEditActivities = React.useMemo(() => {
    if (!codeEditTarget) return [];
    const stagedById = new Map(
      pendingOps.pending_taktifications
        .filter((t) => t.takt_work === codeEditTarget)
        .map((t) => [t.activity_id, t]),
    );
    const removed = new Set(pendingOps.pending_work_code_removals.map((r) => r.activity_id));
    return context.activities
      .filter(
        (a) =>
          isTaskType(a) &&
          !isPlaceholder(a) &&
          !removed.has(a.id) &&
          (a.takt_work === codeEditTarget || stagedById.has(a.id)),
      )
      .map((a) => {
        // Staged zone overlays the (untagged) DB value so a re-zone save keeps
        // the staged Takt_A/B/C instead of nulling them.
        const st = stagedById.get(a.id);
        return {
          id: a.id,
          task_code: a.task_code,
          task_name: a.task_name,
          takt_a: st?.takt_a ?? a.takt_a,
          takt_b: st?.takt_b ?? a.takt_b,
          takt_c: st?.takt_c ?? a.takt_c,
        };
      });
  }, [
    codeEditTarget,
    context.activities,
    pendingOps.pending_taktifications,
    pendingOps.pending_work_code_removals,
  ]);

  // Remove ONE activity from the code being edited: full-untag it (back to the
  // untagged palette) and pull its bar off the board.
  const handleRemoveFromCode = React.useCallback(
    (activityId: number) => {
      pushUndo();
      setPendingOps((prev) => removeFromWorkCode(prev, activityId));
      dropInstancesFor(new Set([activityId]));
    },
    [pushUndo, dropInstancesFor, setPendingOps],
  );

  // Delete the code from this section: full-untag every COMMITTED carrier, drop
  // the drafted side, hide the legend chip, and pull every bar of the code off
  // the board (committed carriers, staged carriers, and drafted new activities).
  const handleDeleteCode = React.useCallback(
    (code: string) => {
      const committedCarrierIds = context.activities
        .filter((a) => isTaskType(a) && !isPlaceholder(a) && a.takt_work === code)
        .map((a) => a.id);
      pushUndo();
      setPendingOps((prev) => deleteWorkCode(prev, code, committedCarrierIds));
      setInstances((prev) => prev.filter((i) => i.activityKey !== code));
    },
    [context.activities, pushUndo, setPendingOps, setInstances],
  );

  // Rename / recolor / re-code the code for this section (chips + bars repaint
  // off the staged edit via the palette memo; no board geometry changes — the
  // re-code shows as a display-only value until publish rewrites Takt_Work).
  const handleEditWorkCode = React.useCallback(
    (code: string, patch: { label?: string; color?: string; newCode?: string }) => {
      pushUndo();
      setPendingOps((prev) => editWorkCode(prev, code, patch));
    },
    [pushUndo, setPendingOps],
  );

  // The legend shows only codes actually present on the board: carried by a
  // live (non-deleted) member or a staged new bar, plus codes minted this
  // session (so a just-added code is placeable). Codes that exist solely on
  // deleted activities drop out; a live member's code the section context
  // never named (e.g. kept work carrying another section's code) gets a
  // minted chip so the legend always matches the bars.
  const visiblePalette = React.useMemo(() => {
    const live = new Set<string>();
    for (const m of protoMembers) {
      // Completed work never renders a bar — its code isn't "on the board".
      if (m.takt_work && m.status !== "TK_Complete") live.add(m.takt_work);
    }
    for (const w of pendingOps.pending_work_code_adds) live.add(w.code);
    const out = palette.filter((p) => live.has(p.activityKey));
    const known = new Set(out.map((p) => p.activityKey));
    for (const code of Array.from(live)) {
      if (!known.has(code)) {
        out.push({
          activityKey: code,
          name: code,
          color: context.takt_work_colors[code] ?? hashColor(code),
          default_duration: 1,
        });
      }
    }
    return out;
  }, [
    palette,
    protoMembers,
    pendingOps.pending_work_code_adds,
    context.takt_work_colors,
  ]);

  // Staged section-local re-codes (palette ✎): canonical code -> displayed
  // code. Bars/lane/inspector RENDER the new value; keys stay canonical.
  const codeDisplay = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of pendingOps.pending_work_code_edits) {
      if (e.new_code && e.new_code !== e.code) m[e.code] = e.new_code;
    }
    return m;
  }, [pendingOps.pending_work_code_edits]);

  // PROTOTYPE: committed colors overlaid with the palette's staged edits, so
  // a recolored work code repaints its bars immediately.
  const effectiveCodeColors = React.useMemo(() => {
    const m: Record<string, string> = { ...(context.takt_work_colors || {}) };
    for (const p of palette) m[p.activityKey] = p.color;
    return m;
  }, [context.takt_work_colors, palette]);

  return {
    palette,
    paletteByKey,
    visiblePalette,
    codeDisplay,
    effectiveCodeColors,
    codeEditTarget,
    setCodeEditTarget,
    codeEditActivities,
    handleAddCodeToPalette,
    handleEditWorkCode,
    handleDeleteCode,
    handleRemoveFromCode,
  };
}
